import React from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { Button, Header } from "../../../components";

import { useAppDispatch } from "../../../redux/store";
import { resetCartItems } from "../../../redux/cart/slice";
import { cartSelector } from "../../../redux/cart/selectors";
import { partnerIdSelector } from "../../../redux/user/selectors";
import { bitrixPaymentObjectSend, createPaymentObject } from "../../../redux/payment/bitrixPayment";
import { convertCartToBitrixItems } from "../../../redux/payment/paymentHelpers";
import { sendFrontendLog } from "../../../redux/frontendLog/asyncActions";

import styles from "./PaymentSuccess.module.scss";
import paymentSuccessImg from "../../../assets/img/paymentSuccessImg.svg";

import {
  removeDateFromStorage,
  removeLastSelectedHallFromStorage,
  removeRentDateFromStorage,
  removeSelectedAdultSubscrFromStorage,
  removeSelectedHallsFromStorage,
  removeSelectedKidSubscrFromStorage,
  removeSelectedSubscrFromStorage,
  removeSubscrFormsDataFromStorage,
  removeTotalPriceFromStorage,
  removeWebPaymentConfirmationTokenFromStorage,
  removeWebPaymentIDFromStorage,
} from "../../../utils";
import {
  resetDate,
  resetLastSelectedHall,
  resetSelectedHalls,
} from "../../../redux/halls/slice";
import {
  resetSelectedAdultSubscr,
  resetSelectedKidSubscr,
  resetSelectedSubscr,
  resetSubscrFormsData,
} from "../../../redux/subscriptions/slice";
import { resetWebPaymentConfToken } from "../../../redux/profile/paytoken/slice";

export const PaymentSuccess: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { basket } = useSelector(cartSelector);
  const partnerId = useSelector(partnerIdSelector);

  const paymentId = (location.state as any)?.paymentId || 
    new URLSearchParams(location.search).get('payment_id') || '';
  const guidFromState = (location.state as any)?.guid || 
    new URLSearchParams(location.search).get('guid') || '';

  React.useEffect(() => {
    dispatch(sendFrontendLog({
      type: "Payment success page loaded",
      data: {
        component: "PaymentSuccess",
        timestamp: new Date().toISOString(),
        url: window.location.pathname,
        userAgent: navigator.userAgent,
        paymentId,
        guid: guidFromState,
        partnerId,
        basket,
        basketItemsCount: Object.keys(basket).length,
      },
    }));

    if (partnerId && paymentId && Object.keys(basket).length > 0) {
      (async () => {
        try {
          dispatch(sendFrontendLog({
            type: "Sending final payment confirmation to Bitrix",
            data: {
              component: "PaymentSuccess",
              timestamp: new Date().toISOString(),
              paymentId,
              guid: guidFromState,
              partnerId: String(partnerId),
              basket,
              itemsCount: Object.keys(basket).length,
            },
          }));

          const bitrixItems = convertCartToBitrixItems(basket, String(partnerId));
          
          const paymentObject = guidFromState 
            ? {
                partnerId: String(partnerId),
                guid: guidFromState,
                changeDate: new Date().toISOString().slice(0, 19),
                items: bitrixItems,
                paymentId: paymentId,
              }
            : createPaymentObject(String(partnerId), paymentId, bitrixItems);
          
          const hasGiftSubscription = bitrixItems.some(item => item.membershipType === 'gift');
          
          if (hasGiftSubscription) {
            try {
              const giftItem = bitrixItems.find(item => item.membershipType === 'gift');
              
              if (giftItem) {
                dispatch(sendFrontendLog({
                  type: "Sending gift subscription data",
                  data: {
                    component: "PaymentSuccess",
                    timestamp: new Date().toISOString(),
                    paymentId,
                    partnerId: String(partnerId),
                    giftItem,
                    hasEmail: !!giftItem.email,
                    hasGiftDate: !!giftItem.giftDate,
                    hasMessage: !!giftItem.message,
                  },
                }));
                
              }
            } catch (giftError: any) {
              dispatch(sendFrontendLog({
                type: "Gift subscription processing error",
                data: {
                  component: "PaymentSuccess",
                  timestamp: new Date().toISOString(),
                  error: giftError?.message || String(giftError),
                  errorResponse: giftError?.response?.data,
                  paymentId,
                  partnerId: String(partnerId),
                },
              }));
            }
          }
          
          await bitrixPaymentObjectSend({
            partnerId: String(partnerId),
            guid: paymentObject.guid,
            changeDate: paymentObject.changeDate,
            items: bitrixItems,
            paymentId: paymentId,
          });
          
          dispatch(sendFrontendLog({
            type: "Bitrix payment confirmation sent successfully",
            data: {
              component: "PaymentSuccess",
              timestamp: new Date().toISOString(),
              paymentId,
              guid: paymentObject.guid,
              partnerId: String(partnerId),
              paymentObject,
              bitrixItems,
              hasGiftSubscription,
            },
          }));
        } catch (error: any) {
          dispatch(sendFrontendLog({
            type: "Bitrix payment confirmation error",
            data: {
              component: "PaymentSuccess",
              timestamp: new Date().toISOString(),
              error: error?.message || String(error),
              errorResponse: error?.response?.data,
              paymentId,
              guid: guidFromState,
              partnerId: String(partnerId),
            },
          }));
        }
      })();
    }
  }, [partnerId, paymentId, basket, dispatch]);

  React.useEffect(() => {
    removeDateFromStorage();
    removeRentDateFromStorage();
    removeTotalPriceFromStorage();
    removeSelectedHallsFromStorage();
    removeSelectedSubscrFromStorage();
    removeSubscrFormsDataFromStorage();
    removeSelectedAdultSubscrFromStorage();
    removeSelectedKidSubscrFromStorage();
    removeWebPaymentConfirmationTokenFromStorage();
    removeWebPaymentIDFromStorage();
    removeLastSelectedHallFromStorage();
    dispatch(resetDate());
    dispatch(resetSelectedHalls());
    dispatch(resetSelectedSubscr());
    dispatch(resetCartItems());
    dispatch(resetSubscrFormsData());
    dispatch(resetSelectedAdultSubscr());
    dispatch(resetSelectedKidSubscr());
    dispatch(resetWebPaymentConfToken());
    dispatch(resetLastSelectedHall());
  }, []);

  return (
    <>
      <Header title={""} />
      <main>
        <section className="container">
          <p className={styles.subTitile}>Оплата прошла успешно</p>
          <div className={styles.image}>
            <img src={paymentSuccessImg} alt="payment success" />
          </div>
          <div className={styles.linkButtons}>
            <Button newStyles={styles.button} to="/profile/subscriptions">
              К списку моих абонементов
            </Button>
            <Button newStyles={styles.button} to="/profile/halls">
              К списку моих арендованных залов
            </Button>
            <Button newStyles={styles.button} to="/subscriptions">
              Купить абонемент
            </Button>
          </div>
        </section>
      </main>
    </>
  );
};
